/**
 * "Detalhes" (D3, D10): a descrição e a subárvore da pasta até a mais profunda,
 * com os perfis e o número de documentos de cada nível. Só para `manage`, `audit`
 * ou admin — e sem título de documento, só contagem (a `audit` não vê títulos).
 */
import { Alert, Badge, Drawer, Group, Skeleton, Stack, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { IconFolder, IconLock } from '@tabler/icons-react'
import { getKbFolderDetalhes } from '@/api/kb'
import { mensagemDeErro } from './acesso'

interface Props {
  pastaId: number | null
  onClose: () => void
}

export function PainelDeDetalhes({ pastaId, onClose }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['kb-folder-detalhes', pastaId],
    queryFn: () => getKbFolderDetalhes(pastaId as number),
    enabled: pastaId !== null,
  })

  return (
    <Drawer opened={pastaId !== null} onClose={onClose} position="right" size="md" title={<Text fw={700}>Detalhes</Text>}>
      {isLoading ? (
        <Stack gap="xs"><Skeleton height={20} /><Skeleton height={60} /><Skeleton height={60} /></Stack>
      ) : error ? (
        <Alert color="red">{mensagemDeErro(error, 'Falha ao carregar os detalhes.')}</Alert>
      ) : data ? (
        <Stack gap="md">
          <Stack gap={2}>
            {data.caminho && data.caminho !== data.nome && <Text size="xs" c="dimmed">{data.caminho}</Text>}
            <Group gap="xs">
              <Text fw={700}>{data.nome}</Text>
              {data.sensivel && <Badge size="xs" color="red" variant="light" leftSection={<IconLock size={10} />}>Sensível</Badge>}
            </Group>
            <Text size="sm" c={data.descricao ? undefined : 'dimmed'}>{data.descricao || 'Sem descrição.'}</Text>
          </Stack>
          <Stack gap="xs" aria-label="Níveis">
            {data.niveis.map((n) => (
              <Stack key={n.id} gap={4} pl={n.profundidade * 16} data-testid={`nivel-${n.id}`}>
                <Group gap={6} wrap="nowrap">
                  <IconFolder size={14} stroke={1.5} />
                  <Text size="sm" fw={600}>{n.nome}</Text>
                  {n.sensivel && <IconLock size={12} stroke={1.5} aria-label="sensível" />}
                  <Text size="xs" c="dimmed" ff="monospace">
                    {n.documentos} {n.documentos === 1 ? 'documento' : 'documentos'}
                  </Text>
                </Group>
                <Group gap={4}>
                  {n.perfis.length === 0 ? (
                    <Text size="xs" c="dimmed">Nenhum perfil: só quem vê tudo lê.</Text>
                  ) : (
                    n.perfis.map((p) => (
                      <Badge key={p.id} size="xs" variant="outline" color="inovareTeal">{p.nome ?? `perfil ${p.id}`}</Badge>
                    ))
                  )}
                </Group>
              </Stack>
            ))}
          </Stack>
        </Stack>
      ) : null}
    </Drawer>
  )
}
